import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { ArrowRight, Sparkles, FlaskConical, ShieldCheck, Smartphone, ChevronRight } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PAGE_SEO } from "@/lib/seoData";
import { SectionHeading, Reveal } from "@/components/Section";
import { ASSETS } from "@/lib/assets";
import { ClosedLoopHero } from "@/components/ClosedLoopHero";
import { ConceptPaper } from "@/components/ConceptPaper";
import { AppIntelligence } from "@/components/AppIntelligence";
import { AppShowcase } from "@/components/AppShowcase";
import { SkinConsole } from "@/components/SkinConsole";
import { SyncDemo } from "@/components/SyncDemo";

const A = "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/";

const EXPLORE_CARDS = [
  {
    to: "/iq",
    eyebrow: "The App",
    title: "WearSpray IQ",
    body: "Help shape the personalized skincare intelligence platform we're building, and register for early access.",
  },
  {
    to: "/science",
    eyebrow: "The Evidence",
    title: "The Science",
    body: "The reapplication research, survey data and dermatology guidance behind WearSpray's design decisions.",
  },
  {
    to: "/patents",
    eyebrow: "The IP",
    title: "Patents",
    body: "The issued U.S. patents covering WearSpray's wearable dispensing and sensing architecture.",
  },
];

const PILLARS = [
  {
    icon: Smartphone,
    title: "Sensing & Context",
    body: "Biometric and environmental signals, UV, activity, skin and time, read to understand the moment, not just the data.",
  },
  {
    icon: FlaskConical,
    title: "AI Personalization",
    body: "An intelligence layer designed to learn routines and responses over time, turning raw signals into a personal recommendation.",
  },
  {
    icon: ShieldCheck,
    title: "Grounded Research",
    body: "Built on dermatology guidance and reapplication science, not assumptions, so the recommendations stay credible.",
  },
];

// Ultra-compact sensor spec module
const SENSOR_VIEWS_IMG = `${A}hsv9qtn0_ACTUAL%20SENSOR%20VIEWS%202.jpg`;

// Shows the static 4-view sensor render, then dissolves to a video whose first
// frame is the identical render, so the sensor visibly slides into the band.
function SensorRevealVideo() {
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  const inView = useInView(wrapRef, { amount: 0.5, once: false });
  const [revealed, setRevealed] = useState(false);
  const [showSense, setShowSense] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!inView) {
      setRevealed(false);
      setShowSense(false);
      if (v) { v.pause(); try { v.currentTime = 0; } catch (e) {} }
      return;
    }
    const onTime = () => {
      if (!v) return;
      setShowSense(v.currentTime >= 7.0 && v.currentTime <= 8.7);
    };
    const t = setTimeout(() => {
      if (v) {
        try { v.currentTime = 0; } catch (e) {}
        v.addEventListener("timeupdate", onTime);
        v.play().catch(() => {});
      }
      setRevealed(true);
    }, 1200);
    return () => {
      clearTimeout(t);
      if (v) v.removeEventListener("timeupdate", onTime);
    };
  }, [inView]);

  return (
    <div ref={wrapRef} className="relative w-full bg-black" style={{ aspectRatio: "1280 / 720" }} data-testid="intelligence-sensor-reveal">
      <video
        ref={videoRef}
        src="/video/sensor-slide.mp4"
        poster="/video/sensor-slide-poster.jpg"
        muted
        playsInline
        preload="auto"
        className="absolute inset-0 w-full h-full object-cover"
        data-testid="intelligence-sensor-video"
      />
      <img loading="lazy"
        src={SENSOR_VIEWS_IMG}
        alt="Four views of the WearSpray sensor module: front, side profile, three-quarter, and back showing the PPG optical window and charging contacts"
        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ease-in-out"
        style={{ opacity: revealed ? 0 : 1, willChange: "opacity" }}
        data-testid="intelligence-sensor-views-image"
      />
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-500 ease-in-out"
        style={{ opacity: showSense ? 1 : 0 }}
        aria-hidden={!showSense}
      >
        {showSense && [0, 0.28, 0.56, 0.84, 1.12].map((delay) => (
          <motion.span
            key={delay}
            className="absolute rounded-full border-2 border-[#00e5ff]"
            style={{ width: "34%", aspectRatio: "1 / 1", boxShadow: "0 0 24px rgba(0,229,255,0.55)", willChange: "transform, opacity" }}
            initial={{ scale: 0.3, opacity: 0.7 }}
            animate={{ scale: 2.8, opacity: 0 }}
            transition={{ duration: 1.4, delay, ease: "easeOut", repeat: Infinity }}
          />
        ))}
        <span
          className="relative font-heading font-semibold text-white tracking-[0.12em] leading-none"
          style={{ fontSize: "clamp(4rem, 15vw, 13rem)", textShadow: "0 0 40px rgba(0,0,0,0.85), 0 8px 30px rgba(0,0,0,0.7)" }}
        >
          SENSE
        </span>
      </div>
    </div>
  );
}

// Future hardware crossfade: wrist as a point of action → smart display concept
const FUT_NO_DISPLAY = `${A}v1wuv6g1_fuiture%20line%201.jpg`;
const FUT_DISPLAY = `${A}ynl0d0rd_fuiture%20line%202.jpg`;
const FUT_STAGES = [
  { img: FUT_NO_DISPLAY, eyebrow: "The Wrist Becomes a Point of Action", title: "Reapply sunscreen, moisturizer or body spray directly from the wrist." },
  { img: FUT_DISPLAY, eyebrow: "Now Imagine If It Knew When to Act", title: "Health intelligence. Environmental awareness. Personal care intelligence. Physical action at the point of need." },
];

const FUT_SPARKLES = [
  { l: "18%", t: "30%", s: 10, d: 0.05 }, { l: "34%", t: "62%", s: 7, d: 0.35 },
  { l: "46%", t: "24%", s: 12, d: 0.15 }, { l: "52%", t: "70%", s: 8, d: 0.5 },
  { l: "63%", t: "38%", s: 9, d: 0.25 }, { l: "71%", t: "58%", s: 11, d: 0.6 },
  { l: "80%", t: "30%", s: 7, d: 0.4 },  { l: "88%", t: "50%", s: 10, d: 0.2 },
  { l: "27%", t: "48%", s: 6, d: 0.7 },  { l: "58%", t: "18%", s: 7, d: 0.55 },
  { l: "40%", t: "44%", s: 8, d: 0.3 },  { l: "93%", t: "34%", s: 6, d: 0.65 },
];

function MagicSparkle({ trigger }) {
  return (
    <div key={trigger} className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      <div
        className="absolute inset-y-0 left-0 w-1/4"
        style={{
          background: "linear-gradient(100deg, transparent, rgba(190,242,255,0.28), rgba(255,255,255,0.5), rgba(190,242,255,0.28), transparent)",
          mixBlendMode: "screen",
          animation: "wsSweep 1.6s cubic-bezier(0.16,1,0.3,1) both",
        }}
      />
      {FUT_SPARKLES.map((p) => (
        <span
          key={`${p.l}-${p.t}`}
          className="absolute"
          style={{
            left: p.l, top: p.t, width: p.s, height: p.s,
            background: "radial-gradient(circle, #ffffff 0%, #9ff0ff 45%, rgba(0,229,255,0) 70%)",
            filter: "drop-shadow(0 0 6px rgba(140,240,255,0.9))",
            animation: `wsTwinkle 1.3s ease-out ${p.d}s both`,
            mixBlendMode: "screen",
          }}
        />
      ))}
    </div>
  );
}

function FutureCrossfade() {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.55, once: true });
  const [stage, setStage] = useState(0);
  useEffect(() => {
    if (inView && stage === 0) {
      const t = setTimeout(() => setStage(1), 2000);
      return () => clearTimeout(t);
    }
  }, [inView, stage]);
  const s = FUT_STAGES[stage];
  return (
    <section ref={ref} className="relative w-full overflow-hidden border-t border-b border-zinc-800 bg-black" data-testid="intelligence-future-crossfade">
      <div className="relative w-full h-[66vh] md:h-[84vh]">
        {FUT_STAGES.map((f, i) => (
          <img
            key={f.eyebrow}
            src={f.img}
            alt={i === stage ? "WearSpray band lineup" : ""}
            aria-hidden={i !== stage}
            loading="eager"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-[1200ms] ease-in-out"
            style={{ opacity: i === stage ? 1 : 0 }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/25 to-black/35 pointer-events-none" />
        {stage === 1 && <MagicSparkle trigger={stage} />}
        <AnimatePresence>
          {stage === 1 && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="absolute top-5 right-5 md:top-8 md:right-8"
            >
              <span className="inline-flex items-center gap-2 font-mono text-[11px] md:text-xs tracking-[0.22em] uppercase text-accent bg-black/70 border border-accent/50 px-3.5 py-2 backdrop-blur-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                Future Integration
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div className="absolute bottom-0 left-0 w-full">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pb-14 md:pb-24">
          <AnimatePresence mode="wait">
            <motion.div
              key={stage}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
              <p className="font-mono text-xs tracking-[0.28em] uppercase text-accent">{s.eyebrow}</p>
              <p className="font-heading text-2xl md:text-4xl font-light tracking-tight text-white mt-3 max-w-3xl leading-snug">{s.title}</p>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

// SCIENCE → SENSING → AI → PERSONALIZATION → FORMULATIONS → ACTION, converging into WearSpray
const CONVERGENCE_NODES = [
  { label: "Science", c: "#00e5ff" },
  { label: "Sensing", c: "#3ad6a0" },
  { label: "AI", c: "#e6c068" },
  { label: "Personalization", c: "#ff2d78" },
  { label: "Formulations", c: "#00e5ff" },
  { label: "Action", c: "#ff2d78" },
];

function ConvergenceDiagram() {
  return (
    <div className="mt-16 md:mt-20" data-testid="intelligence-convergence-diagram">
      <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-4 md:gap-x-3">
        {CONVERGENCE_NODES.map((n, i) => {
          const isLast = i === CONVERGENCE_NODES.length - 1;
          return (
            <div key={n.label} className="flex items-center gap-2 md:gap-3">
              {isLast ? (
                <motion.span
                  className="relative font-mono font-bold text-sm md:text-base tracking-[0.14em] uppercase px-6 md:px-7 py-3 md:py-3.5 rounded-full text-black"
                  style={{ background: n.c }}
                  animate={{ scale: [1, 1.09, 1], boxShadow: [`0 0 0px ${n.c}00`, `0 0 34px ${n.c}bb, 0 0 60px ${n.c}55`, `0 0 0px ${n.c}00`] }}
                  transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                >
                  {n.label}
                </motion.span>
              ) : (
                <span
                  className="font-mono text-[11px] md:text-xs tracking-[0.14em] uppercase px-3.5 py-2 rounded-full border"
                  style={{ color: n.c, borderColor: `${n.c}55`, background: `${n.c}0d` }}
                >
                  {n.label}
                </span>
              )}
              {!isLast && <ChevronRight size={14} className="text-zinc-600 shrink-0" aria-hidden="true" />}
            </div>
          );
        })}
      </div>
      <div className="flex flex-col items-center mt-8 md:mt-10">
        <span className="w-px h-10 md:h-14 bg-gradient-to-b from-zinc-600 to-transparent" aria-hidden="true" />
        <div
          className="relative flex items-center gap-2.5 rounded-full border border-accent/60 bg-black px-6 py-3"
          style={{ boxShadow: "0 0 50px rgba(0,229,255,0.35), 0 0 90px rgba(255,45,120,0.15)" }}
        >
          <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
          <span className="font-heading text-lg md:text-xl font-medium tracking-tight text-white">WearSpray</span>
        </div>
        <p className="font-body text-zinc-500 text-sm mt-5 text-center max-w-md">The platform where these disciplines meet.</p>
      </div>
    </div>
  );
}

export default function Intelligence() {
  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden" data-testid="intelligence-page">
      <SEO {...PAGE_SEO["/intelligence"]} path="/intelligence" />

      {/* HERO */}
      <section className="relative border-b border-zinc-800 overflow-hidden" data-testid="intelligence-hero">
        <div className="absolute inset-0 z-0">
          <img
            loading="eager"
            src="https://images.unsplash.com/photo-1434493789847-2f02dc6ca35d?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Person wearing a connected smartwatch"
            className="absolute inset-0 w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/80 to-black" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 pt-28 pb-16 md:pt-36 md:pb-20">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.28em] uppercase text-accent block mb-7">Technology</span>
            <h1 className="font-heading font-light tracking-tight leading-[1.1] text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
              Closing the gap between awareness and action.
            </h1>
            <p className="font-body text-zinc-400 text-lg md:text-xl leading-relaxed mt-7 max-w-2xl">
              WearSpray IQ, AI personalization and contextual sensing are a cohesive technology
              ecosystem in development, grounded in dermatology research and the issued patents
              already protecting the underlying architecture. The vision: personalized
              recommendations that arrive at the exact moment they can be acted on.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CONVERGENCE */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pt-16 md:pt-20" data-testid="intelligence-convergence-section">
        <ConvergenceDiagram />
      </section>

      {/* THREE PILLARS */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28" data-testid="intelligence-pillars-section">
        <SectionHeading overline="The Ecosystem" title="Three disciplines, one connected intelligence layer." />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <div className="h-full border border-zinc-800 bg-[#0d0d0d] p-7 rounded-xl">
                <p.icon size={22} className="text-accent" />
                <h3 className="font-heading text-lg font-medium text-white mt-5">{p.title}</h3>
                <p className="font-body text-zinc-400 text-sm leading-relaxed mt-4">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-14 border border-zinc-800 bg-[#0d0d0d] rounded-xl p-7 flex items-start gap-4">
            <Sparkles size={19} className="text-accent shrink-0 mt-0.5" />
            <p className="font-body text-sm text-zinc-400 leading-relaxed">
              WearSpray IQ, sensing hardware and AI recommendations remain in development. No
              clinical or health outcome is claimed or implied until formally evaluated, and UV
              index or physiological signals alone do not establish actual sunscreen protection
              or skin condition. "WearSpray Sense" is the proposed name for the companion app
              interface within this same WearSpray IQ intelligence layer, not a separate product.
            </p>
          </div>
        </Reveal>
      </section>

      {/* DETAILED SENSOR ARCHITECTURE */}
      <section className="border-t border-zinc-900 bg-[#0a0a0a]" data-testid="intelligence-sensor-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionHeading
            overline="The Technology Behind The Intelligence"
            title="Ultra-compact sensor architecture."
            subtitle="Designed around currently available wearable sensor technology, built to integrate discreetly into the existing delivery platform rather than replace it."
          />
          <Reveal delay={0.08}>
            <div className="relative mt-12">
              <div className="absolute -inset-8 blur-3xl opacity-30 pointer-events-none" style={{ background: "radial-gradient(circle, rgba(0,229,255,0.14), transparent 65%)" }} />
              <div className="relative border border-zinc-800 bg-black overflow-hidden">
                <SensorRevealVideo />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* THE CLOSED-LOOP SYSTEM · full cinematic Sense → Understand → Prompt → Act → Learn */}
      <ClosedLoopHero />

      {/* SYNCHRONIZED BAND + PHONE DEMO */}
      <SyncDemo />

      {/* CONCEPT PAPER · downloadable contextual skin intelligence framework */}
      <ConceptPaper />

      {/* PLATFORM CAPABILITIES IN DEVELOPMENT */}
      <AppIntelligence />

      {/* PERSONAL SKIN INTELLIGENCE · the ten AI consultant concepts */}
      <SkinConsole />

      {/* INSIDE THE APP · REAL SCREEN RECORDING */}
      <AppShowcase />

      {/* FUTURE HARDWARE CROSSFADE */}
      <FutureCrossfade />

      {/* EXPLORE DEEPER */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28" data-testid="intelligence-explore-section">
        <SectionHeading overline="Go Deeper" title="Explore the technology in detail." />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
          {EXPLORE_CARDS.map((c, i) => (
            <Reveal key={c.to} delay={i * 0.08}>
              <Link
                to={c.to}
                data-testid={`intelligence-explore-card-${i}`}
                className="group block h-full border border-zinc-800 bg-[#0d0d0d] p-7 rounded-xl hover:border-accent/40 transition-colors"
              >
                <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent">{c.eyebrow}</span>
                <h3 className="font-heading text-xl font-medium text-white mt-4">{c.title}</h3>
                <p className="font-body text-sm text-zinc-400 leading-relaxed mt-3">{c.body}</p>
                <span className="inline-flex items-center gap-2 font-body text-sm text-white mt-6 group-hover:gap-3 transition-all">
                  Visit page <ArrowRight size={14} />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-zinc-900 bg-[#0a0a0a]" data-testid="intelligence-cta-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <Reveal>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-3xl mx-auto">
              Help shape the intelligence layer.
            </h2>
            <Link
              to="/iq?src=technology"
              data-testid="intelligence-cta-link"
              className="inline-flex items-center gap-2 font-body text-sm font-medium px-8 py-4 mt-9 text-white transition-colors"
              style={{ background: "#ff2d78" }}
            >
              Join WearSpray IQ
              <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
